import React, { Fragment, useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { MoreVertical, PlusCircle } from "react-feather";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import CommonModal from "../../../UiKits/Modals/common/modal";
import { Spinner } from "../../../../AbstractElements";
import { baseURL } from "../../../../Services/api/baseURL";
import { SeoMetaFields } from "../../../Common/SeoMetaFields";
import { getStaticPageSeoPreviewUrl } from "../../../../utils/seoPreviewUrls";
import useRbac from "../../../../Hooks/useRbac";

const spinnerData = [{ id: 33, heading: "Loader 31", spinnerClass: "loader-35" }];

const SEO_PAGES_API = `${baseURL}/api/seo/pages`;

const truncate = (value = "", max = 80) =>
  value.length > max ? `${value.slice(0, max)}…` : value;

const getApiErrorMessage = (error, fallback = "Failed to save SEO page") => {
  const message = error.response?.data?.message;
  if (typeof message === "string") return message;
  return (
    message?.details?.[0]?.message ||
    message?.message ||
    fallback
  );
};

const SeoStaticPagesTable = () => {
  const { can } = useRbac();
  const [pages, setPages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [pageId, setPageId] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [listError, setListError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [resetPagination, setResetPagination] = useState(false);
  const editRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const mutationRequestRef = useRef(0);

  const toggleModal = () => {
    editRequestRef.current += 1;
    mutationRequestRef.current += 1;
    formik.resetForm();
    setPageId("");
    setEditLoading(false);
    setModalOpen((isOpen) => !isOpen);
  };

  const getToken = () => JSON.parse(localStorage.getItem("token"));

  const fetchPages = async (
    page = currentPage,
    limit = rowsPerPage,
    search = searchTerm
  ) => {
    const requestId = listRequestRef.current + 1;
    listRequestRef.current = requestId;
    const token = getToken();
    setLoading(true);
    try {
      const response = await axios.get(SEO_PAGES_API, {
        headers: { Authorization: `${token}` },
        params: {
          page,
          limit,
          ...(search.trim() && { search: search.trim() }),
        },
      });
      if (listRequestRef.current !== requestId) return;
      const payload = response.data?.data ?? response.data ?? [];
      setPages(Array.isArray(payload) ? payload : []);
      setTotalRows(response.data?.total ?? payload.length);
      setListError("");
    } catch (error) {
      if (listRequestRef.current !== requestId) return;
      console.error(error);
      setListError(getApiErrorMessage(error, "Failed to load static SEO pages"));
    } finally {
      if (listRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const requestTimer = setTimeout(() => {
      fetchPages(currentPage, rowsPerPage, searchTerm);
    }, 300);

    return () => clearTimeout(requestTimer);
  }, [currentPage, rowsPerPage, searchTerm]);

  const loadPage = async (id) => {
    const requestId = editRequestRef.current + 1;
    editRequestRef.current = requestId;
    const token = getToken();
    setEditLoading(true);
    try {
      const response = await axios.get(`${SEO_PAGES_API}/${id}`, {
        headers: { Authorization: `${token}` },
      });
      if (editRequestRef.current !== requestId) return;
      const data = response.data?.data ?? response.data;
      formik.setValues({
        path: data.path || "",
        metaTitle: data.metaTitle || "",
        metaDescription: data.metaDescription || "",
        robots: data.robots || "",
        status: data.status || "active",
      });
    } catch (error) {
      if (editRequestRef.current !== requestId) return;
      setModalOpen(false);
      setPageId("");
      Swal.fire({
        icon: "error",
        title: getApiErrorMessage(error, "Failed to load SEO page"),
        confirmButtonColor: "#d3178a",
      });
    } finally {
      if (editRequestRef.current === requestId) {
        setEditLoading(false);
      }
    }
  };

  const openEditModal = (page) => {
    mutationRequestRef.current += 1;
    formik.resetForm();
    setPageId(page._id);
    setModalOpen(true);
    loadPage(page._id);
  };

  const deletePage = async (page) => {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Delete this SEO page?",
      text: `${page.path} will become inactive.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d3178a",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const token = getToken();
      const response = await axios.delete(`${SEO_PAGES_API}/${page._id}`, {
        headers: { Authorization: `${token}` },
      });
      Swal.fire({
        icon: "success",
        title: response.data?.message || "SEO page deleted",
        confirmButtonColor: "#d3178a",
      });
      fetchPages(currentPage, rowsPerPage, searchTerm);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: getApiErrorMessage(error, "Failed to delete SEO page"),
        confirmButtonColor: "#d3178a",
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      path: "",
      metaTitle: "",
      metaDescription: "",
      robots: "",
      status: "active",
    },
    validationSchema: Yup.object({
      path: Yup.string()
        .required("Path is required")
        .matches(/^\//, "Path must start with /"),
    }),
    onSubmit: async (values) => {
      const requestId = mutationRequestRef.current + 1;
      mutationRequestRef.current = requestId;
      const token = getToken();
      const body = {
        path: values.path,
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        robots: values.robots || "",
        status: values.status,
      };

      try {
        const response = pageId
          ? await axios.patch(`${SEO_PAGES_API}/${pageId}`, body, {
            headers: { Authorization: `${token}` },
          })
          : await axios.post(SEO_PAGES_API, body, {
            headers: { Authorization: `${token}` },
          });

        if (mutationRequestRef.current !== requestId) return;
        Swal.fire({
          icon: "success",
          title: response.data?.message || "SEO page saved",
          confirmButtonColor: "#d3178a",
        });
        toggleModal();
        fetchPages(currentPage, rowsPerPage, searchTerm);
      } catch (error) {
        if (mutationRequestRef.current !== requestId) return;
        Swal.fire({
          icon: "error",
          title: getApiErrorMessage(error),
          confirmButtonColor: "#d3178a",
        });
      }
    },
  });

  const columns = [
    {
      name: "#",
      width: "55px",
      center: true,
      cell: (_row, index) => (currentPage - 1) * rowsPerPage + index + 1,
    },
    {
      name: "Path",
      selector: (row) => row.path,
      sortable: true,
      width: "240px",
      cell: (row) => (
        <button
          type="button"
          className={`border-0 bg-transparent p-0 text-start ${can("seo_pages.update") ? "text-primary" : ""
            }`}
          style={{ cursor: can("seo_pages.update") ? "pointer" : "default" }}
          onClick={() => can("seo_pages.update") && openEditModal(row)}
        >
          {row.path}
        </button>
      ),
    },
    {
      name: "Meta Title",
      selector: (row) => row.metaTitle,
      sortable: true,
      minWidth: "220px",
      cell: (row) => truncate(row.metaTitle, 50),
    },
    {
      name: "Meta Description",
      selector: (row) => row.metaDescription,
      sortable: true,
      grow: 2,
      cell: (row) => truncate(row.metaDescription, 80),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span className={row.status === "active" ? "text-success" : "text-danger"}>
          {row.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "80px",
      center: true,
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            {can("seo_pages.update") && (
              <DropdownItem onClick={() => openEditModal(row)}>
                Edit <FaPen />
              </DropdownItem>
            )}
            {can("seo_pages.delete") && row.status === "active" && (
              <DropdownItem className="text-danger" onClick={() => deletePage(row)}>
                Delete <FaTrashAlt />
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      omit: !can("seo_pages.update") && !can("seo_pages.delete"),
    },
  ];

  return (
    <Fragment>
      <Row xxl={12} className="pb-4">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Static Page SEO</h4>
          <div className="d-flex align-items-center">
            <div className="form-group position-relative search_outer d-flex align-items-center mb-0">
              <i className="fa fa-search"></i>
              <input
                className="form-control border-0"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                  setResetPagination((shouldReset) => !shouldReset);
                }}
                type="text"
                placeholder="Search path..."
              />
            </div>
            {can("seo_pages.create") && (
              <Button className="btn btn-primary d-flex align-items-center ms-3" onClick={toggleModal}>
                <PlusCircle />
                Add Page
              </Button>
            )}
          </div>
        </div>
      </Row>

      {pages.length > 0 ? (
        <DataTable
          data={pages}
          columns={columns}
          striped
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationResetDefaultPage={resetPagination}
          paginationPerPage={rowsPerPage}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangePage={setCurrentPage}
          onChangeRowsPerPage={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setCurrentPage(1);
            setResetPagination((shouldReset) => !shouldReset);
          }}
        />
      ) : (
        !loading &&
        !listError && <p className="text-center my-5">No static SEO pages found</p>
      )}

      {listError && <p className="text-danger text-center my-4">{listError}</p>}

      {loading &&
        spinnerData.map((spinner) => (
          <Col xxl="12" key={spinner.id} className="flex justify-center items-center">
            <div className="loader-box">
              <Spinner attrSpinner={{ className: spinner.spinnerClass }} />
            </div>
          </Col>
        ))}

      <CommonModal
        isOpen={modalOpen}
        title={pageId ? "Edit Static Page SEO" : "Add Static Page SEO"}
        className="store_modal"
        toggler={toggleModal}
        size="md"
      >
        <Container>
          <Form onSubmit={formik.handleSubmit}>
            <FormGroup>
              <Label>
                Path <span className="text-danger">*</span>
              </Label>
              <Input
                name="path"
                value={formik.values.path}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="/faq"
                disabled={editLoading}
              />
              {formik.touched.path && formik.errors.path ? (
                <span className="text-danger">{formik.errors.path}</span>
              ) : null}
            </FormGroup>
            <SeoMetaFields
              metaTitle={formik.values.metaTitle}
              metaDescription={formik.values.metaDescription}
              onMetaTitleChange={formik.handleChange}
              onMetaDescriptionChange={formik.handleChange}
              urlPreview={getStaticPageSeoPreviewUrl(formik.values.path)}
            />
            <FormGroup>
              <Label>Robots (optional)</Label>
              <Input
                name="robots"
                value={formik.values.robots}
                onChange={formik.handleChange}
                placeholder="noindex, nofollow"
              />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Input
                type="select"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Input>
            </FormGroup>
            <Row>
              <Col xxl={12} className="text-center">
                <Button
                  type="submit"
                  className="mt-4"
                  disabled={editLoading || formik.isSubmitting}
                >
                  {editLoading
                    ? "Loading..."
                    : pageId
                      ? "Update Page SEO"
                      : "Save Page SEO"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </CommonModal>
    </Fragment>
  );
};

export default SeoStaticPagesTable;
